import {
  ApiResponse,
  ApiSchemas,
  createAsyncedStore,
  createSharedRoot,
  InitDepends,
  Setting,
  useSetSetting,
  setting,
} from '@bz/request';
import { makePersisted } from '@solid-primitives/storage';
import { isServer } from 'solid-js/web';
import { createWritableMemo } from '@solid-primitives/memo';
import { Suspense } from 'solid-js';
import Decimal from 'decimal.js-light';

import { mountExchangeRate, mountWalletSettings } from './currency-exchange';

import WidgetLink from '~/components/WidgetLink';

export function UserContext(props: ParentProps) {
  return (
    <InitDepends
      sources={[
        mountAccount,
        mountOddsPreset,
        mountWalletSettings,
        mountCurrencyConfig,
        revalidateCurrencyRecommend,
        mountExchangeRate,
        mountAllViewCurrency,
        mountSports,
        mountInviteRequirementSwitchPublic,
        mountCashoutRules,
      ]}
    >
      <Suspense>
        {WidgetLink()}
        {props.children}
      </Suspense>
    </InitDepends>
  );
}

const defaultAccount: ApiResponse<'getCurrentUser'> & { isLogin: boolean } = {
  userId: -1,
  userName: '',
  email: '',
  phone: '',
  avatar: '',
  emailVerified: false,
  phoneVerified: false,
  isLogin: false,
  twoFaVerified: false,
  passkeySet: false,
  isOldDevice: false,
};

export type SimpleSportResult = Pick<ApiResponse<'getLeaguesWithOdds'>[number], 'logoUrl' | 'name' | 'id'> & {
  children: SimpleSportResult[];
};

export const [inviteRequirementSwitchPublic, mountInviteRequirementSwitchPublic] = createAsyncedStore<
  ApiResponse<'InviteRequirementSwitchPublic'>
>(async () => {
  const res = await get.InviteRequirementSwitchPublic().catch(() => {
    return { inviteCodeRequired: true };
  });
  return res;
});

export const [initCashoutRules, mountCashoutRules] = createAsyncedStore<
  ApiResponse<'getTicketsCashoutRules'> & { lastUpdated: number }
>(async () => {
  const res = await get.getTicketsCashoutRules().catch(() => {
    return { rules: [], unsupportedBetTypes: [] };
  });
  return { ...res, lastUpdated: Date.now() };
});

const [account, mountAccount] = createAsyncedStore<ApiResponse<'getCurrentUser'> & { isLogin: boolean }>(async () => {
  const account = await get.getCurrentUser().catch(() => undefined);
  return account ? { ...account, isLogin: true } : defaultAccount;
});

const [oddsPreset, mountOddsPreset] = createAsyncedStore<ApiResponse<'getOddsBzSetting'>>(async () => {
  return await get.getOddsBzSetting().catch(() => ({}) as ApiResponse<'getOddsBzSetting'>);
});

const [globalSports, mountSports] = createAsyncedStore(async () => {
  const sports = await getOnce.getSportDataSportTree().catch((err) => {
    ssrLog('===catch===getOnce.getSportDataSportTree error:', err);
    return [];
  });
  const flatSports = sports.flatMap((sport) => sport.categories).flatMap((category) => category.tournaments);

  return { sports, flatSports };
});

type WalletConfig = ApiSchemas['AccountCurrencyInfo'];
type CurrencyConfig = ApiResponse<'getAllCurrency'>[number];
type AllViewCurrency = ApiResponse<'getAllViewCurrency'>[number];
type CurrencyRecommend = ApiResponse<'getAccountRecommendList'>;

const [currencyConfig, mountCurrencyConfig] = createAsyncedStore<Record<string, CurrencyConfig>>(async () => {
  const source = await get.getAllCurrency().catch(() => []);

  return (source ?? []).reduce(
    (res, it) => {
      res[it.currencyCode] = it;
      return res;
    },
    {} as Record<string, CurrencyConfig>
  );
});

export const [allViewCurrency, mountAllViewCurrency] = createAsyncedStore<Record<string, AllViewCurrency>>(async () => {
  const source = await get.getAllViewCurrency().catch(() => []);
  return (source ?? []).reduce(
    (res, it) => {
      res[it.currencyCode] = it;
      return res;
    },
    {} as Record<string, AllViewCurrency>
  );
});

export { account, oddsPreset, currencyConfig, globalSports, currencyRecommend, setCurrencyRecommend, defaultAccount };

export const useFavList = createSharedRoot(() => {
  const [favListLeagueMode, setFavListLeagueMode] = createSignal<number | undefined>(1);
  const [favListSportId, setFavListSportId] = createSignal<string | undefined>();

  const list = useResource<ApiResponse<'UserFollowMatchList'>>(
    async () => {
      if (account.isLogin) {
        const mode = favListLeagueMode();
        const sportId = favListSportId();
        let marketMode = setting.marketMode;
        if (sportId) {
          const modeConfig = oddsPreset.marketModes?.[sportId];
          if (modeConfig && !modeConfig.includes(setting.marketMode)) {
            marketMode = modeConfig[modeConfig.length - 1];
          }
        }
        const queryParams = {
          ...(mode !== undefined && { leagueMode: mode }),
          ...(marketMode !== undefined && { marketMode: marketMode }),
        };
        // @ts-ignore
        return get.UserFollowMatchList(queryParams);
      } else {
        return [];
      }
    },
    { initialValue: [] }
  );

  const [ids, setIds] = createWritableMemo(() => {
    const ret: string[] = [];
    list().map((item: any) => {
      item.matches.map((match: any) => {
        ret.push(match.id);
      });
      item.finishedVOS?.map((match: any) => {
        ret.push(match.id);
      });
    });
    return ret;
  });
  return {
    ids,
    setIds,
    list,
    loading: list.loading,
    favListLeagueMode,
    setFavListLeagueMode,
    favListSportId,
    setFavListSportId,
  };
});
export const useSupportGroup = createSharedRoot(() => {
  const groupInfo = useResource<ApiResponse<'getUserIntercomGroup'>>(
    async (pre) => {
      if (account.isLogin) {
        return get.getUserIntercomGroup();
      } else {
        return pre;
      }
    },
    { initialValue: {} as ApiResponse<'getUserIntercomGroup'> }
  );
  return groupInfo;
});
const initialValue = {
  cash: { fiatCurrencyList: [], cryptoCurrencyList: [] },
  bonus: {},
  vault: { fiatCurrencyList: [], cryptoCurrencyList: [] },
  currencyMap: {},
} as unknown as CurrencyRecommend;

const [currencyRecommend, revalidateCurrencyRecommend, setCurrencyRecommend] = createAsyncedStore<
  ApiResponse<'getAccountRecommendList'> & { currencyMap: Record<string, WalletConfig> }
>(async () => {
  const ret = await get.getAccountRecommendList().catch(() => undefined);

  if (ret) {
    const currencyMap = ret.cash.cryptoCurrencyList.concat(ret.cash.fiatCurrencyList).reduce(
      (map, curr) => {
        map[curr.currencyCode] = curr;
        return map;
      },
      {} as Record<string, WalletConfig>
    );

    return {
      ...ret,
      currencyMap,
      bonus: {
        ...ret.bonus,
        currencyList: ret.bonus?.currencyList?.map((item) => ({ ...item, balanceType: 'bonus' })),
      },
    };
  }
  return { ...initialValue, currencyMap: {} };
});
const defaultLastUsedCurrency = { fiatDeposit: null, cryptoDeposit: null };
type CreditAccountInfoResultType = { hasCredit: boolean } & ApiSchemas['CreditAccountInfoResult'];
const defaultCreditInfo = {
  availableBalance: 0,
  totalUnpaidAmount: 0,
  totalBalance: 0,
  frozenBalance: 0,
  billed: false,
  periodStartDisplay: '',
  periodEndDisplay: '',
  nextPeriodStartDisplay: '',
  accountStatus: 0,
  hasCredit: false,
} as CreditAccountInfoResultType;
export const repayEnum = {
  UNPAID: 1,
  NOTIFY: 2,
  OVERDUE: 3,
};
export const accountStatusEnum = {
  NORMAL: 1,
  DISABLE: 0,
};
export const useCurrency = createSharedRoot((currencyCode?: string) => {
  const setSetting = useSetSetting();

  const lastUsedCurrency = useResource(
    async () => {
      if (account.isLogin) {
        const res = await get.getLastUsedChannelAll();
        return res;
      } else {
        return defaultLastUsedCurrency;
      }
    },
    { initialValue: defaultLastUsedCurrency }
  );
  const walletCreditInfo = useResource<CreditAccountInfoResultType>(
    async () => {
      if (account.isLogin) {
        const res = await get.getWalletCreditInfo().catch(() => defaultCreditInfo);
        if (res) {
          (res as CreditAccountInfoResultType).hasCredit = true;
          return res as CreditAccountInfoResultType;
        } else {
          return defaultCreditInfo;
        }
      } else {
        return defaultCreditInfo;
      }
    },
    { initialValue: defaultCreditInfo }
  );

  const latestCurrency = createMemo(() => {
    const { fiatDeposit, cryptoDeposit } = lastUsedCurrency();

    const currency = setting.currency || fiatDeposit?.currencyCode || cryptoDeposit?.currencyCode || 'USDT';
    return currency;
  });
  const totalAvailableDecimalBalance = createMemo(() => {
    const total = Object.values(currencyRecommend.currencyMap || {}).reduce((sum, item) => {
      const itemBalance = new Decimal(item.balance);
      return sum.add(currency.convertCurrency(itemBalance, item.currencyCode, 'USDT'));
    }, new Decimal(0));
    return total;
  });
  const currencyAvailableCreditBalance = createMemo(() => {
    const vipCreditBalance = walletCreditInfo()?.availableBalance || 0;
    const currencyBalance = currency.convertCurrency(new Decimal(vipCreditBalance), 'USDT', latestCurrency());
    return currencyBalance?.toNumber();
  });
  function setCurrency(currencyCode: string, type: Setting.BalanceType = Setting.BalanceType.cash) {
    setSetting({ currency: currencyCode, balanceType: type });
  }

  function setBalanceType(type: Setting.BalanceType) {
    setSetting({ balanceType: type });
  }

  const sourceMap = {
    cash: 'BALANCE',
    bonus: 'BONUS',
  };

  const balanceType = createMemo(() => setting.balanceType || Setting.BalanceType.cash);
  const isCash = createMemo(() => balanceType() === Setting.BalanceType.cash);
  const isBonus = createMemo(() => balanceType() === Setting.BalanceType.bonus);
  const source = createMemo(() => sourceMap[balanceType()]);

  const BET_CREDIT_COOKIE_NAME = 'is_bet_credit_on';

  const cookieStorage = {
    getItem: (key: string) => {
      if (isServer) {
        return null;
      } else {
        const item = document.cookie.split('; ').find((cookieItem) => cookieItem.startsWith(`${key}=`));
        return item ? item.split('=')[1] : null;
      }
    },
    setItem: (key: string, value: string) => {
      if (!isServer) {
        document.cookie = `${key}=${value}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`;
      }
    },
    removeItem: (key: string) => {
      if (!isServer) {
        document.cookie = `${key}=; path=/; max-age=0; SameSite=Lax`;
      }
    },
  };

  const [isBetCreditOn, setIsBetCreditOn] = makePersisted(createSignal(false), {
    name: BET_CREDIT_COOKIE_NAME,
    storage: cookieStorage,
  });

  const obj = {
    latestCurrency: latestCurrency,
    setCurrency,
    source,
    isCash,
    isBonus,
    setBalanceType,
    balanceType,
    isBetCreditOn,
    setIsBetCreditOn,
    walletCreditInfo,
    currencyAvailableCreditBalance,
  };
  try {
    const addVipBalance = createMemo(() => {
      const key = currencyCode || latestCurrency();
      if (isCash()) {
        const balance = currencyRecommend.currencyMap?.[key]?.balance ?? 0;
        if (isBetCreditOn()) {
          const vipCreditBalance = walletCreditInfo()?.availableBalance || 0;
          const currencyBalance = currency.convertCurrency(new Decimal(vipCreditBalance), 'USDT', latestCurrency());
          const totalBalance = new Decimal(currencyBalance).add(new Decimal(balance))?.toNumber();
          return totalBalance;
        }
        return balance;
      }
      return currencyRecommend.bonus?.currencyList?.find((item) => item.currencyCode === key)?.balance ?? 0;
    });
    const isOverRepayDate = createMemo(() => {
      return walletCreditInfo()?.repaymentStatus === repayEnum.OVERDUE;
    });

    const creditBalance = createMemo(() => {
      const vipCreditBalance = walletCreditInfo()?.availableBalance || 0;
      const currencyBalance = currency.convertCurrency(new Decimal(vipCreditBalance), 'USDT', latestCurrency());
      const latestBalance = new Decimal(currencyBalance)?.toNumber();
      return latestBalance;
    });

    const currencyCashBalanceDecimal = createMemo(() => {
      const key = currencyCode || latestCurrency();
      const balance = new Decimal(currencyRecommend.currencyMap?.[key]?.balance ?? 0);
      return balance;
    });

    const totalUsdtBalance = createMemo(() => {
      const total = Object.values(currencyRecommend.currencyMap || {}).reduce((sum, item) => {
        const usdtValue = currency.convertCurrency(new Decimal(item.balance), item.currencyCode, 'USDT').toNumber();
        return sum + usdtValue;
      }, 0);
      return total?.toFixed(2) || '0.00';
    });
    const hasCredit = createMemo(() => {
      return walletCreditInfo()?.hasCredit || false;
    });
    return {
      ...obj,
      addVipBalance,
      hasCredit,
      creditBalance,
      currencyCashBalanceDecimal,
      isOverRepayDate,
      totalUsdtBalance,
      totalAvailableDecimalBalance,
    };
  } catch (error) {
    console.log('🚀 ~ error:', error);
    return {
      ...obj,
      addVipBalance: () => 0,
      hasCredit: () => false,
      creditBalance: () => 0,
      currencyCashBalanceDecimal: () => new Decimal(0),
      totalAvailableDecimalBalance: () => new Decimal(0),
      isOverRepayDate: () => false,
      totalUsdtBalance: () => '0.00',
    };
  }
});
